import Clarity from '@microsoft/clarity'
import { applyIdentify, applyTrack, type ClaritySurface } from './adapt.js'

export interface ClarityPluginConfig {
  projectId: string
}

interface IdentifyPayload { payload: { userId: string; traits?: Record<string, unknown> } }
interface TrackPayload { payload: { event: string; properties?: Record<string, unknown> } }

/**
 * Drive Clarity's recording on/off via the global command queue. The npm
 * `@microsoft/clarity` wrapper only exposes init/identify/setTag/event — start
 * and stop live on `window.clarity(cmd)` (installed synchronously as a queue
 * stub, so commands ordering is preserved even before the remote tag loads).
 */
function clarityCommand(command: 'start' | 'stop'): void {
  if (typeof window === 'undefined') return
  const w = window as unknown as { clarity?: (cmd: string) => void }
  try {
    w.clarity?.(command)
  } catch {
    /* fail-soft */
  }
}

/**
 * Self-contained `analytics` plugin for Microsoft Clarity. Imports ONLY
 * `@microsoft/clarity` — no app/Nuxt coupling — so it can be lifted into its
 * own repo / published as `@aurumrun/analytics-plugin-clarity`.
 *
 * Clarity `event()` is name-only; every property/trait becomes a `setTag`.
 *
 * Recording can be paused (game route — Clarity's MutationObserver thrashes the
 * main thread during the PixiJS RAF loop → flicker). One ordering caveat: an
 * emit queued while paused flushes a frame later, so it can land after an emit
 * that ran synchronously just after a resume. Harmless here — Clarity's custom
 * id is session-scoped, not per-event. While paused, each emit is
 * bracketed with start→…→stop so the custom event/tags still reach Clarity
 * without leaving session replay on. Toggled via the facade's setRecording().
 */
export function clarityPlugin(config: ClarityPluginConfig) {
  const clarity = Clarity as unknown as ClaritySurface & { init(id: string): void }
  let paused = false
  let unloadFlushInstalled = false

  // Run an emit, briefly resuming recording around it when paused so the
  // queued event/tags are captured, then stopping again.
  //
  // The bracket is expensive: `clarity('start')` re-runs the engine's
  // `layout.start() -> discover()`, a full DOM traversal and serialisation,
  // measured at 27-44 ms on a 4x-throttled CPU. Every one of these emits is
  // triggered by a user action (login_modal_opened, deposit_modal_opened,
  // real_bet_placed, demo_played), so run synchronously it lands in the very
  // frame the tap is waiting on and inflates INP (#855).
  //
  // rAF-then-timeout is the "after the next paint" idiom: the rAF callback runs
  // before the frame is painted, and the timeout it schedules therefore runs
  // after it. The event is delayed by roughly one frame, which is far too short
  // to lose it to a navigation, and it can no longer be counted in the
  // interaction it belongs to. Unpaused emits are untouched — they are cheap.
  // Queued emits waiting for the post-paint flush. Batched rather than one
  // bracket each: `start()` is the expensive half, so N events fired from one
  // interaction should cost one `discover()`, not N.
  let queued: Array<() => void> = []
  let flushScheduled = false

  function flush(): void {
    if (queued.length === 0) return
    const batch = queued
    queued = []
    flushScheduled = false
    // `paused` is re-read HERE, not captured at schedule time: setRecording(true)
    // can land in between (PlayPageContent's onBeforeUnmount fires it when the
    // user leaves the game route). Running the stale branch would issue a `stop`
    // after recording was legitimately resumed and silently kill session capture
    // for the rest of the session.
    if (!paused) {
      for (const fn of batch) fn()
      return
    }
    clarityCommand('start')
    try {
      for (const fn of batch) fn()
    } finally {
      clarityCommand('stop')
    }
  }

  function scheduleFlush(): void {
    if (flushScheduled) return
    flushScheduled = true
    // A hidden document paints nothing, so there is no INP to protect and rAF
    // would not fire until the tab is foregrounded — deferring there only risks
    // losing the event to an unload. Same where rAF does not exist (SSR, node).
    const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden'
    if (hidden || typeof requestAnimationFrame !== 'function') {
      flush()
      return
    }
    // rAF-then-timeout is the "after the next paint" idiom: the rAF callback runs
    // before the frame is painted, so the task it queues runs after it. The event
    // is delayed by about one frame and can no longer be counted in the
    // interaction it belongs to.
    requestAnimationFrame(() => {
      setTimeout(flush, 0)
    })
  }

  /**
   * Flush synchronously when the page is going away. `loginWithOAuth` tracks
   * `login_google_clicked` and then redirects to the provider, so without this
   * the one-frame deferral could swallow the event that starts the OAuth funnel.
   */
  function installUnloadFlush(): void {
    if (typeof window === 'undefined' || unloadFlushInstalled) return
    unloadFlushInstalled = true
    window.addEventListener('pagehide', flush)
  }

  function emit(fn: () => void): void {
    if (!config.projectId) return
    if (!paused) {
      fn()
      return
    }
    queued.push(fn)
    scheduleFlush()
  }

  return {
    name: 'clarity',
    initialize(_config?: unknown): void {
      if (!config.projectId) return
      try {
        clarity.init(config.projectId)
        installUnloadFlush()
      } catch {
        /* fail-soft */
      }
    },
    identify({ payload }: IdentifyPayload): void {
      if (!config.projectId || !payload.userId) return
      emit(() => applyIdentify(clarity, payload.userId, payload.traits ?? {}))
    },
    track({ payload }: TrackPayload): void {
      if (!config.projectId) return
      emit(() => applyTrack(clarity, payload.event, payload.properties ?? {}))
    },
    // Non-standard control hook — NOT an `analytics` lifecycle event (never
    // dispatched, so the core ignores it). Invoked directly by the facade's
    // setRecording(). Disabling stops session capture but keeps the event
    // bracket path alive; enabling resumes normal recording.
    setRecording(enabled: boolean): void {
      if (!config.projectId) return
      paused = !enabled
      clarityCommand(enabled ? 'start' : 'stop')
    },
  }
}
